import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import Dialog from "../DialogFunctional";
import MUITextField from "@mui/material/TextField";
import MUIList from "@mui/material/List";
import MUIListItemButton from "@mui/material/ListItemButton";
import MUIListItemText from "@mui/material/ListItemText";
import MUIListItemIcon from "@mui/material/ListItemIcon";
import MUIListSubheader from "@mui/material/ListSubheader";
import MUITypography from "@mui/material/Typography";
import MUIInputAdornment from "@mui/material/InputAdornment";
import MUIBox from "@mui/material/Box";
import MUIIconButton from "@mui/material/IconButton";
import MUISearchIcon from "@mui/icons-material/Search";
import MUIFavoriteIcon from "@mui/icons-material/Favorite";
import MUIFavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import Setting from "../../Utility/Setting";
import Security from "../../Utility/Security";
import MainContext from "../../Context/MainContext/MainContext";
import Department from "../../Utility/Crud/Department";
import Team from "../../Utility/Crud/Team";
import TeamQueue from "../../Utility/Crud/TeamQueue";
import TeamUser from "../../Utility/Crud/TeamUser";
import Queue from "../../Utility/Crud/Queue";

/**
 * Navigation dialog for searching and navigating to pages.
 * Opened via Ctrl+K. Supports favorites, recents, and search.
 *
 * @prop {boolean} open - Whether the dialog is open
 * @prop {function} onClose - Callback when dialog closes
 * @prop {object} pages - Pages configuration from getPagesConfig()
 */
function DialogNavigation({ open, onClose, pages = {} }) {
  const history = useHistory();
  const context = useContext(MainContext);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [favorites, setFavorites] = useState([]);
  const [recents, setRecents] = useState([]);
  const itemRefs = useRef([]);

  /**
   * Flatten the nested pages configuration into a searchable array.
   * Filters out pages marked with excludeFromSearch or lacking permission.
   */
  const allPages = useMemo(() => {
    const flattenedPages = [];

    Object.entries(pages).forEach(([, section]) => {
      if (section.subpages) {
        section.subpages.forEach((page) => {
          if (page.excludeFromSearch) {
            return;
          }
          if (!page.permission || Security.hasPermission(page.permission)) {
            flattenedPages.push({
              id: page.href,
              title: page.title,
              href: page.href,
              section: section.title,
              category: page.category || null,
            });
          }
        });
      } else if (section.href) {
        if (section.excludeFromSearch) {
          return;
        }
        if (!section.permission || Security.hasPermission(section.permission)) {
          flattenedPages.push({
            id: section.href,
            title: section.title,
            href: section.href,
            section: null,
            category: null,
          });
        }
      }
    });

    const queuePages = [];
    const queuePageIds = new Set();
    if (context && context.currentUser && context.departmentsIndexed) {
      Department.sort(Object.values(context.departmentsIndexed)).forEach(
        (department) => {
          const teams = Team.read(context.teamsIndexed, [
            {
              key: "departmentId",
              value: department.id,
            },
          ]);

          if (Security.hasPermission("queue.viewAll") === false) {
            const teamUsers = TeamUser.read(context.teamUsersIndexed, [
              {
                key: "teamId",
                value: teams.map((team) => team.id),
              },
              {
                key: "userId",
                value: context.currentUser.id,
              },
            ]);

            const teamsCurrentUserManages = Team.read(context.teamsIndexed, [
              {
                key: "departmentId",
                value: department.id,
              },
              {
                key: "managerId",
                value: context.currentUser.id,
              },
            ]);

            if (
              teamUsers.length === 0 &&
              teamsCurrentUserManages.length === 0
            ) {
              return;
            }
          }

          const teamQueues = TeamQueue.read(context.teamQueuesIndexed, [
            {
              key: "teamId",
              value: teams.map((team) => team.id),
            },
          ]);

          const accessibleQueues = Queue.sort(
            Queue.read(context.queuesIndexed, [
              {
                key: "id",
                value: teamQueues.map((teamQueue) => teamQueue.queueId),
              },
              {
                key: "queueTypeTermId",
                value: [1, 2], // Production and Error queue types shown on dashboard.
              },
            ]),
          );

          accessibleQueues.forEach((queue) => {
            const queueHref = `/queue/${queue.id}`;
            if (queuePageIds.has(queueHref)) {
              return;
            }
            queuePageIds.add(queueHref);
            queuePages.push({
              id: queueHref,
              title: queue.name,
              href: queueHref,
              section: "Queue",
              category: department.name,
              isQueue: true,
            });
          });
        },
      );
    }

    const allSearchablePages = [...flattenedPages, ...queuePages];
    return allSearchablePages.sort((a, b) => a.title.localeCompare(b.title));
  }, [context, pages]);

  useEffect(() => {
    if (open) {
      setFavorites(Setting.get("meta.favoritePages") || []);
      setRecents(Setting.get("meta.recentPages") || []);
    }
  }, [open]);

  function handleDialogClose() {
    setSearchQuery("");
    setSelectedIndex(0);
    itemRefs.current = [];
    if (onClose) {
      onClose();
    }
  }

  /**
   * Filter and sort pages based on search query.
   * Results are sorted: exact match first, then starts with, then contains.
   */
  function getSearchResults(query) {
    const normalizedQuery = query.toLowerCase().trim();

    if (!normalizedQuery) {
      return [];
    }

    const matchingPages = allPages.filter((page) => {
      return (
        page.title.toLowerCase().includes(normalizedQuery) ||
        (page.section &&
          page.section.toLowerCase().includes(normalizedQuery)) ||
        (page.category && page.category.toLowerCase().includes(normalizedQuery))
      );
    });

    return matchingPages.sort((a, b) => {
      const aTitle = a.title.toLowerCase();
      const bTitle = b.title.toLowerCase();

      if (aTitle === normalizedQuery && bTitle !== normalizedQuery) return -1;
      if (bTitle === normalizedQuery && aTitle !== normalizedQuery) return 1;

      if (
        aTitle.startsWith(normalizedQuery) &&
        !bTitle.startsWith(normalizedQuery)
      )
        return -1;
      if (
        bTitle.startsWith(normalizedQuery) &&
        !aTitle.startsWith(normalizedQuery)
      )
        return 1;

      return aTitle.localeCompare(bTitle);
    });
  }

  /**
   * Get pages that are in favorites.
   */
  function getFavoritePages() {
    return allPages.filter((page) => favorites.includes(page.id));
  }

  /**
   * Get pages that are in recents, preserving the order they were visited.
   */
  function getRecentPages() {
    return recents.reduce((result, href) => {
      const page = allPages.find((entry) => entry.id === href);
      if (page) {
        result.push(page);
      }
      return result;
    }, []);
  }

  /**
   * Get all items currently displayed, used for both rendering and keyboard navigation.
   */
  function getDisplayedItems() {
    const trimmedSearchQuery = searchQuery.trim();

    if (trimmedSearchQuery) {
      return getSearchResults(trimmedSearchQuery);
    }

    const favoritePages = getFavoritePages();
    const recentPages = getRecentPages()
      .filter((page) => !favorites.includes(page.id))
      .slice(0, 3);
    const nonQueuePages = allPages.filter((page) => !page.isQueue);
    const queuePages = allPages
      .filter((page) => page.isQueue)
      .sort((a, b) => {
        const categoryCompare = (a.category || "").localeCompare(
          b.category || "",
        );
        if (categoryCompare !== 0) {
          return categoryCompare;
        }
        return a.title.localeCompare(b.title);
      });

    return [...favoritePages, ...recentPages, ...nonQueuePages, ...queuePages];
  }

  /**
   * Scroll the item at the given index into view.
   */
  function scrollItemIntoView(index) {
    if (itemRefs.current[index]) {
      itemRefs.current[index].scrollIntoView({ block: "nearest" });
    }
  }

  /**
   * Add a page to recents. If already present, moves it to the front.
   */
  function addToRecents(pageHref) {
    const MAX_RECENTS = 10;
    let updatedRecents = Setting.get("meta.recentPages") || [];

    updatedRecents = updatedRecents.filter((href) => href !== pageHref);
    updatedRecents.unshift(pageHref);
    updatedRecents = updatedRecents.slice(0, MAX_RECENTS);

    Setting.set("meta.recentPages", updatedRecents);
    setRecents(updatedRecents);
  }

  /**
   * Handle selecting a page: add to recents and navigate.
   */
  function handleSelect(page, options = {}) {
    const { openInNewTab = false } = options;
    addToRecents(page.id);
    handleDialogClose();
    if (openInNewTab) {
      window.open(page.href, "_blank", "noopener,noreferrer");
      return;
    }
    history.push(page.href);
  }

  /**
   * Toggle favorite status for a page.
   */
  function toggleFavorite(e, pageHref) {
    e.stopPropagation();

    const updatedFavorites = [...(Setting.get("meta.favoritePages") || [])];
    const index = updatedFavorites.indexOf(pageHref);

    if (index > -1) {
      updatedFavorites.splice(index, 1);
    } else {
      updatedFavorites.push(pageHref);
    }

    Setting.set("meta.favoritePages", updatedFavorites);
    setFavorites(updatedFavorites);
  }

  /**
   * Check if a page is favorited.
   */
  function isFavorite(pageHref) {
    return favorites.includes(pageHref);
  }

  /**
   * Handle search input change.
   */
  function handleSearchChange(e) {
    setSearchQuery(e.target.value);
    setSelectedIndex(0);
  }

  /**
   * Handle keyboard events for arrow navigation and selection.
   */
  function handleKeyDown(e) {
    const items = getDisplayedItems();
    const maxIndex = items.length - 1;

    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault();
        const newIndex = Math.min(selectedIndex + 1, maxIndex);
        scrollItemIntoView(newIndex);
        setSelectedIndex(newIndex);
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        const newIndex = Math.max(selectedIndex - 1, 0);
        scrollItemIntoView(newIndex);
        setSelectedIndex(newIndex);
        break;
      }
      case "Enter":
        e.preventDefault();
        if (items.length > 0 && selectedIndex < items.length) {
          handleSelect(items[selectedIndex], {
            openInNewTab: e.ctrlKey || e.metaKey,
          });
        }
        break;
      default:
        break;
    }
  }

  /**
   * Render a single page item. Also stores a ref for scroll-into-view.
   */
  function renderPageItem(page, globalIndex) {
    const isSelected = globalIndex === selectedIndex;
    const favorited = isFavorite(page.id);

    let breadcrumb = page.section || "";
    if (page.category) {
      breadcrumb = breadcrumb
        ? `${breadcrumb} > ${page.category}`
        : page.category;
    }

    return (
      <MUIListItemButton
        key={`${page.id}-${globalIndex}`}
        selected={isSelected}
        onClick={(e) =>
          handleSelect(page, {
            openInNewTab: e.ctrlKey || e.metaKey,
          })
        }
        ref={(element) => {
          itemRefs.current[globalIndex] = element;
        }}
        sx={{
          "&.Mui-selected": {
            backgroundColor: "action.selected",
          },
        }}
      >
        <MUIListItemIcon sx={{ minWidth: 32 }}>
          <MUIIconButton
            size="small"
            onClick={(e) => toggleFavorite(e, page.id)}
            sx={{ p: 0.5 }}
          >
            {favorited ? (
              <MUIFavoriteIcon fontSize="small" sx={{ color: "error.main" }} />
            ) : (
              <MUIFavoriteBorderIcon
                fontSize="small"
                sx={{ color: "text.secondary" }}
              />
            )}
          </MUIIconButton>
        </MUIListItemIcon>
        <MUIListItemText
          primary={page.title}
          slotProps={{
            primary: { variant: "body2" },
          }}
        />
        {breadcrumb && (
          <MUITypography
            variant="caption"
            sx={{
              color: "text.secondary",
              ml: 1,
              flexShrink: 0,
            }}
          >
            {breadcrumb}
          </MUITypography>
        )}
      </MUIListItemButton>
    );
  }

  function renderHeader() {
    return (
      <MUITextField
        autoFocus
        fullWidth
        placeholder="Search pages..."
        variant="standard"
        value={searchQuery}
        onChange={handleSearchChange}
        onKeyDown={handleKeyDown}
        slotProps={{
          input: {
            startAdornment: (
              <MUIInputAdornment position="start">
                <MUISearchIcon />
              </MUIInputAdornment>
            ),
            disableUnderline: true,
          },
        }}
      />
    );
  }

  function renderContent() {
    const trimmedSearchQuery = searchQuery.trim();
    let globalIndex = 0;
    itemRefs.current = [];

    if (trimmedSearchQuery) {
      const matchingPages = getSearchResults(trimmedSearchQuery);

      if (matchingPages.length === 0) {
        return (
          <MUIBox sx={{ p: 2, textAlign: "center" }}>
            <MUITypography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              No pages found for "{trimmedSearchQuery}"
            </MUITypography>
          </MUIBox>
        );
      }

      return (
        <MUIList
          dense
          sx={{ pt: 0, bgcolor: "background.paper" }}
          subheader={
            <MUIListSubheader
              sx={{ lineHeight: "32px", bgcolor: "background.paper" }}
            >
              Search Results
            </MUIListSubheader>
          }
        >
          {matchingPages.map((page) => {
            const item = renderPageItem(page, globalIndex);
            globalIndex++;
            return item;
          })}
        </MUIList>
      );
    }

    const favoritePages = getFavoritePages();
    const recentPages = getRecentPages()
      .filter((page) => !favorites.includes(page.id))
      .slice(0, 3);
    const nonQueuePages = allPages.filter((page) => !page.isQueue);
    const queuePages = allPages
      .filter((page) => page.isQueue)
      .sort((a, b) => {
        const categoryCompare = (a.category || "").localeCompare(
          b.category || "",
        );
        if (categoryCompare !== 0) {
          return categoryCompare;
        }
        return a.title.localeCompare(b.title);
      });

    return (
      <MUIBox sx={{ bgcolor: "background.paper" }}>
        {favoritePages.length > 0 && (
          <MUIList
            dense
            subheader={
              <MUIListSubheader
                sx={{ lineHeight: "32px", bgcolor: "background.paper" }}
              >
                Favorites
              </MUIListSubheader>
            }
          >
            {favoritePages.map((page) => {
              const item = renderPageItem(page, globalIndex);
              globalIndex++;
              return item;
            })}
          </MUIList>
        )}

        {recentPages.length > 0 && (
          <MUIList
            dense
            subheader={
              <MUIListSubheader
                sx={{ lineHeight: "32px", bgcolor: "background.paper" }}
              >
                Recent
              </MUIListSubheader>
            }
          >
            {recentPages.map((page) => {
              const item = renderPageItem(page, globalIndex);
              globalIndex++;
              return item;
            })}
          </MUIList>
        )}

        <MUIList
          dense
          subheader={
            <MUIListSubheader
              sx={{ lineHeight: "32px", bgcolor: "background.paper" }}
            >
              All Pages
            </MUIListSubheader>
          }
        >
          {nonQueuePages.map((page) => {
            const item = renderPageItem(page, globalIndex);
            globalIndex++;
            return item;
          })}
        </MUIList>

        {queuePages.length > 0 && (
          <MUIList
            dense
            subheader={
              <MUIListSubheader
                sx={{ lineHeight: "32px", bgcolor: "background.paper" }}
              >
                Queues
              </MUIListSubheader>
            }
          >
            {queuePages.map((page) => {
              const item = renderPageItem(page, globalIndex);
              globalIndex++;
              return item;
            })}
          </MUIList>
        )}
      </MUIBox>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      renderHeader={renderHeader}
      renderContent={renderContent}
      renderActions={() => null}
      getMaxWidth={() => "sm"}
      getDividers={() => true}
      getContentSx={() => ({
        p: 0,
        height: 400,
        bgcolor: "background.paper",
        overflowY: "auto",
        overflowX: "hidden",
      })}
    />
  );
}

export default DialogNavigation;
